import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { openCopilot } from '@/features/copilot';
import { useDatasetScore } from '@/features/scoring';
import type { NodeKind } from '@/shared/api/generated/lineage';
import { SCREEN_PATHS } from '@/shared/routes';
import { HelpLabel } from '@/shared/ui/InfoTip';

import { useNodeReach } from '../api/lineage';
import type { GraphLayout } from '../model/graph';
import { relatedTo } from '../model/graph';
import type { NodeHealth } from './LineageGraph';

/* The design's panel beside the graph: what the selected node is, how far it reaches, and what would
 * notice if it broke.
 *
 * **Nothing selected is a state of its own** and says how to get out of it, rather than falling back
 * to the subject: a reader who cleared the selection asked for the whole picture at full strength.
 *
 * **Every dataset in the graph can be opened**, the subject or a neighbour — the walk carries each
 * reached node's dataset id, so a neighbour's quality and its dataset are one click away rather than
 * one search away. A job belongs to no connection and is not scored, and both are said in words.
 *
 * The up and down figures are the node's reach across the whole graph, from the tenant's reach read,
 * not just what the picture drew; the affected list is the drawn downstream datasets, each with its
 * health dot.
 */

interface Focus {
  readonly id: string;
  readonly kind: NodeKind;
  readonly name: string;
  readonly datasetId: string | null;
  readonly catalogued: boolean;
  readonly connection: { readonly name: string; readonly platform: string } | null;
}

export function LineageFocusPanel({
  layout,
  picked,
  health,
  onClear,
}: {
  readonly layout: GraphLayout;
  readonly picked: string | null;
  readonly health: ReadonlyMap<string, NodeHealth>;
  readonly onClear: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const reach = useNodeReach();

  const node =
    picked === null ? undefined : layout.nodes.find((each) => each.id === picked);

  if (node === undefined) {
    return (
      <aside className="lq-ln__focus" aria-label={t('lineage.focus.label')}>
        <p className="lq-ln__focusNone">{t('lineage.focus.nothing')}</p>
      </aside>
    );
  }

  const focus: Focus = {
    id: node.id,
    kind: node.kind,
    name: node.label,
    datasetId: node.datasetId,
    catalogued: node.catalogued,
    connection: node.connection,
  };
  const counts = reach.data?.nodes.find((each) => each.node_id === focus.id);
  const downstream = relatedTo(layout, focus.id).downstream;
  const affected = layout.nodes.filter(
    (each) => downstream.has(each.id) && each.kind === 'dataset',
  );

  return (
    <aside className="lq-ln__focus" aria-labelledby="lineage-focus-name">
      <div className="lq-ln__focusHead">
        <div className="lq-ln__focusIdentity">
          <div className="lq-ln__focusKind">{t(`lineage.kind.${focus.kind}`)}</div>
          <div id="lineage-focus-name" className="lq-ln__focusName">
            {focus.name}
          </div>
          <div className="lq-ln__focusWhere">
            {focus.connection !== null
              ? focus.connection.name
              : t(
                  focus.kind === 'job'
                    ? 'lineage.focus.jobNoConnection'
                    : 'lineage.focus.oneHarvestAway',
                )}
          </div>
        </div>
        <button
          type="button"
          className="lq-ln__focusClear"
          aria-label={t('lineage.focus.clear')}
          onClick={onClear}
        >
          ×
        </button>
      </div>

      <div className="lq-ln__focusFigures">
        <div>
          <div className="lq-ln__focusValue lq-ln__up">
            {counts === undefined ? '—' : String(counts.upstream)}
          </div>
          <div className="lq-ln__focusLabel">{t('lineage.focus.upstream')}</div>
        </div>
        <div>
          <div className="lq-ln__focusValue lq-ln__down">
            {counts === undefined ? '—' : String(counts.downstream)}
          </div>
          <div className="lq-ln__focusLabel">{t('lineage.focus.downstream')}</div>
        </div>
        <div>
          <FocusQuality focus={focus} />
          <div className="lq-ln__focusLabel">{t('lineage.focus.quality')}</div>
        </div>
      </div>

      {affected.length > 0 ? (
        <div>
          <div className="lq-ln__focusLabel lq-ln__focusLabel--head">
            <HelpLabel
              inline
              subject={t('lineage.focus.affected')}
              help={t('help.term.impact')}
            >
              <span>{t('lineage.focus.affected')}</span>
            </HelpLabel>
          </div>
          <ul className="lq-ln__affected">
            {affected.map((each) => (
              <li key={each.id}>
                <span
                  className="lq-ln__affectedDot"
                  data-health={
                    each.datasetId === null
                      ? 'none'
                      : (health.get(each.datasetId) ?? 'none')
                  }
                  aria-hidden="true"
                />
                {each.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="lq-ln__focusActions">
        {focus.datasetId === null ? null : (
          <Link
            className="lq-btn lq-btn--primary lq-btn--compact"
            to={generatePath(SCREEN_PATHS.dataset, { datasetId: focus.datasetId })}
          >
            {t('lineage.focus.openDataset')}
          </Link>
        )}
        {/* Opens the shell's drawer with the question in the composer, unsent. */}
        <button
          type="button"
          className="lq-btn lq-btn--copilot lq-btn--compact"
          onClick={() => {
            openCopilot({
              question: t('lineage.focus.askQuestion', { name: focus.name }),
            });
          }}
        >
          {t('lineage.focus.ask')}
        </button>
      </div>
    </aside>
  );
}

function FocusQuality({ focus }: { readonly focus: Focus }): ReactElement {
  const { t } = useTranslation();
  /* Not asked for a node with no dataset: a job or an uncatalogued table would be a guaranteed 404
     that reads as "never scored" about something that cannot be. */
  const score = useDatasetScore(focus.datasetId ?? '');
  if (focus.datasetId === null) {
    return (
      <div className="lq-ln__focusValue lq-ln__focusValue--words">
        {t(
          focus.kind === 'job'
            ? 'lineage.focus.jobsUnscored'
            : 'lineage.focus.neverScored',
        )}
      </div>
    );
  }
  if (score.isPending) {
    return <div className="lq-ln__focusValue lq-ln__focusValue--words">…</div>;
  }
  if (score.isError) {
    return (
      <div className="lq-ln__focusValue lq-ln__focusValue--words">
        {t('lineage.focus.neverScored')}
      </div>
    );
  }
  return (
    <div
      className={
        score.data.value === null
          ? 'lq-ln__focusValue lq-ln__focusValue--words'
          : 'lq-ln__focusValue'
      }
    >
      {score.data.value === null
        ? t('lineage.focus.notComputable')
        : score.data.value.toFixed(1)}
    </div>
  );
}
